import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import NoteAddOutlined from "@mui/icons-material/NoteAddOutlined";
import {
  Box,
  Button,
  IconButton,
  List,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SKILL_ENTRYPOINT } from "encamp-shared/src/s3/skills/paths";

import type { SkillFileTreeController } from "./useSkillFileEditor";

import { SkillFileTreeRows } from "./SkillFileTreeRows";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useSkillTreeEntryEditing, type SkillTreeEntryEditing } from "./useSkillTreeEntryEditing";

/** Placeholder rows while the file list loads; same row height as a dense tree row. */
const SKELETON_TREE_ROW_WIDTHS = ["55%", "70%", "45%"];

/** Fixed so switching skills or files never resizes the dialog; the panes scroll instead. */
const FILE_PANES_HEIGHT = 440;

/**
 * The tree and editor keep a fixed size whatever the skill's files, with
 * skeletons in place of the rows and content while they load. Key it by
 * skill so a half-named entry or empty folder doesn't carry over.
 */
export function SkillFileTreeEditor({
  editor,
  readOnly = false,
}: {
  editor: SkillFileTreeController;
  readOnly?: boolean;
}) {
  const editing = useSkillTreeEntryEditing(editor);
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      sx={{ height: { md: FILE_PANES_HEIGHT } }}
    >
      <SkillFileTreePane editor={editor} editing={editing} readOnly={readOnly} />
      <SkillFileContentPane
        editor={editor}
        namingNewFile={editing.namingNewFile}
        readOnly={readOnly}
      />
      <UnsavedChangesDialog {...editor.unsavedChangesDialog} />
    </Stack>
  );
}

function SkillFileTreeToolbar({
  editing,
  disabled,
}: {
  editing: SkillTreeEntryEditing;
  disabled: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ pl: 1.5, pr: 0.5, py: 0.25, borderBottom: 1, borderColor: "divider" }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontWeight: 600 }}>
        Files
      </Typography>
      <Tooltip title="New file">
        <span>
          <IconButton
            size="small"
            aria-label="New file"
            disabled={disabled}
            onClick={() => void editing.startNew("file")}
          >
            <NoteAddOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="New folder">
        <span>
          <IconButton
            size="small"
            aria-label="New folder"
            disabled={disabled}
            onClick={() => void editing.startNew("folder")}
          >
            <CreateNewFolderOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function SkillFileTreePane({
  editor,
  editing,
  readOnly,
}: {
  editor: SkillFileTreeController;
  editing: SkillTreeEntryEditing;
  readOnly: boolean;
}) {
  return (
    <Box
      sx={{
        width: { xs: "100%", md: 260 },
        height: { xs: 240, md: "auto" },
        flexShrink: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {readOnly ? null : (
        <SkillFileTreeToolbar editing={editing} disabled={editor.loading || editor.writing} />
      )}
      <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0.5 }}>
        {editor.loading ? (
          SKELETON_TREE_ROW_WIDTHS.map((width, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 2, py: 1 }}
            >
              <Skeleton variant="rounded" width={18} height={18} />
              <Skeleton variant="text" width={width} />
            </Stack>
          ))
        ) : (
          <SkillFileTreeRows
            nodes={editing.tree}
            depth={0}
            folder=""
            selectedPath={editor.selectedPath}
            expandedFolders={editor.expandedFolders}
            readOnly={readOnly}
            editing={editing}
          />
        )}
      </List>
    </Box>
  );
}

/** Header text: the open file, or a hint while a new file still needs a name. */
function contentTitle(editor: SkillFileTreeController, namingNewFile: boolean): string {
  if (namingNewFile) {
    return "New file: name it in the tree to save";
  }
  if (!editor.selectedPath) {
    return "Select a file";
  }
  return editor.dirty ? `${editor.selectedPath} (unsaved)` : editor.selectedPath;
}

function SkillFileContentPane({
  editor,
  namingNewFile,
  readOnly,
}: {
  editor: SkillFileTreeController;
  namingNewFile: boolean;
  readOnly: boolean;
}) {
  const busy = editor.loading || editor.loadingContent;
  const editable = !readOnly && !busy && (Boolean(editor.selectedPath) || namingNewFile);

  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      {/* Tall enough for the small buttons, so the row doesn't grow when they appear. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 32 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
          {editor.loading ? (
            <Skeleton variant="text" width={140} />
          ) : (
            contentTitle(editor, namingNewFile)
          )}
        </Typography>
        {!readOnly && editor.selectedPath && editor.selectedPath !== SKILL_ENTRYPOINT && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutline />}
            disabled={editor.deleting}
            onClick={() => void editor.handleDelete()}
          >
            Delete
          </Button>
        )}
        {!readOnly && editor.selectedPath && (
          <Button
            size="small"
            variant="contained"
            disabled={!editor.dirty || editor.writing || busy}
            onClick={() => void editor.handleSave()}
          >
            Save file
          </Button>
        )}
      </Stack>
      {/* Fills the pane and scrolls its content, so long files don't grow it. */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <TextField
          value={busy ? "" : editor.draft}
          onChange={(event) => editor.setDraft(event.target.value)}
          disabled={!editable}
          multiline
          rows={14}
          fullWidth
          sx={{ height: { md: "100%" } }}
          InputProps={{
            sx: {
              fontFamily: "monospace",
              fontSize: 13,
              height: { md: "100%" },
              alignItems: "flex-start",
              "& textarea": { height: { md: "100% !important" }, overflow: "auto !important" },
            },
          }}
        />
        {busy && (
          <Skeleton
            variant="rounded"
            data-testid="skill-file-content-skeleton"
            sx={{ position: "absolute", inset: 0, height: "auto" }}
          />
        )}
      </Box>
    </Stack>
  );
}
